import { Form, Link, useActionData, useNavigation, useSearchParams } from "@remix-run/react";
import { useEffect, useRef, useState } from "react";
import {
  TextInput, PasswordInput, Checkbox, Button, Group,
  Stack, Title, Text, Anchor, Divider, rem, Alert
} from '@mantine/core';
import { useForm, hasLength, isEmail } from '@mantine/form';
import { IconAt, IconLock, IconAlertCircle, IconBuilding, IconLogin2 } from '@tabler/icons-react';

import type { action as loginAction } from "./action.server";
import loginBg01 from "~/assets/images/login-bg-01.jpg";

export { loader } from "./loader.server";
export { action } from "./action.server";

export const meta = () => [
  { title: "Hub Desk - Giriş" },
  { name: "description", content: "Hub Desk uygulaması giriş sayfası" },
];

export const handle = {
  backgroundImage: loginBg01,
};

export default function Login() {
  const actionData = useActionData<typeof loginAction>();
  const [searchParams] = useSearchParams();
  const navigation = useNavigation();
  const isSubmitting = navigation.formAction === "/login" && navigation.state === "submitting";
  const [isAlertVisible, setIsAlertVisible] = useState(true); // Alert görünürlüğü için state

  const redirectTo = searchParams.get("redirectTo") || "/dashboard";
  const message = searchParams.get("message");
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const form = useForm({
    initialValues: {
      email: actionData?.fields?.email ?? (process.env.NODE_ENV === 'development' ? 'admin@root.com' : ''),
      password: actionData?.fields?.password ?? (process.env.NODE_ENV === 'development' ? '123Pa$$word!' : ''),
      tenant: actionData?.fields?.tenant ?? (process.env.NODE_ENV === 'development' ? 'root' : ''),
      remember: actionData?.fields?.rememberMe ?? false,
    },
    validate: {
      email: isEmail('Geçerli bir e-posta adresi giriniz'),
      password: hasLength({ min: 6 }, 'Şifre en az 6 karakter olmalıdır'),
      tenant: (value) => !value ? 'Tenant seçimi zorunludur' : null,
    },
  });

  useEffect(() => {
    if (actionData?.errors?.email) emailRef.current?.focus();
    else if (actionData?.errors?.password) passwordRef.current?.focus();

    // Form hatası varsa alert'i göster
    if (actionData?.errors?.form) {
      setIsAlertVisible(true);
    }
  }, [actionData]);

  return (
    <div>
      {message === "password-reset-success" && (
        <Alert
          icon={<IconAlertCircle size={24} />}
          title="Başarılı"
          variant="filled"
          color="green"
          mb="md"
          withCloseButton
          onClose={() => setIsAlertVisible(false)}
        >
          Şifreniz başarıyla sıfırlandı. Yeni şifrenizle giriş yapabilirsiniz.
        </Alert>
      )}

      {actionData?.errors?.form && isAlertVisible && (
        <Alert
          icon={<IconAlertCircle size={24} />}
          title="Hata"
          variant="filled"
          color="red"
          mb="md"
          withCloseButton
          onClose={() => setIsAlertVisible(false)} // Alert'i gizle
        >
          {actionData.errors.form}
        </Alert>
      )}

      <Form method="post" replace>
        <input type="hidden" name="redirectTo" value={redirectTo} />
        <Stack gap="lg">
          <Group justify="space-between">
            <Title order={2} c={"white"}>Giriş Yap</Title>
            <IconLogin2 color="white" size={48}></IconLogin2>
          </Group>
          <Stack gap="xs">
            <Text size="xs" c={"white"}>Tenant</Text>
            <TextInput
              placeholder="Tenant adını giriniz"
              leftSection={<IconBuilding style={{ width: rem(18), height: rem(18) }} />}
              name="tenant"
              error={actionData?.errors?.tenant || form.errors.tenant}
              disabled={isSubmitting}
              {...form.getInputProps('tenant')}
              data-testid="tenant-input"
              size="md"
            />
          </Stack>
          <Stack gap="xs">
            <Text size="xs" c={"white"}>E-posta</Text>
            <TextInput
              size="md"
              ref={emailRef}

              placeholder="ornek@email.com"
              leftSection={<IconAt style={{ width: rem(18), height: rem(18) }} />}
              name="email"
              error={actionData?.errors?.email || form.errors.email}
              disabled={isSubmitting}
              {...form.getInputProps('email')}
              autoComplete="email"
              data-testid="email-input"
            />
          </Stack>

          <Stack gap="xs">
            <Text size="xs" c={"white"}>Şifre</Text>
            <PasswordInput
              size="md"
              ref={passwordRef}

              placeholder="Şifreniz"
              leftSection={<IconLock style={{ width: rem(18), height: rem(18) }} />}
              name="password"
              error={actionData?.errors?.password || form.errors.password}
              disabled={isSubmitting}
              {...form.getInputProps('password')}
              autoComplete="current-password"
              data-testid="password-input"
            />
          </Stack>




          <Button type="submit" mt="md" color="blue" radius="md" fullWidth loading={isSubmitting} data-testid="login-button">
            {isSubmitting ? "Giriş Yapılıyor..." : "Giriş Yap"}
          </Button>

          <Group justify="center" mt="md">
            <Anchor component={Link} to="/forgot-password" size="sm" c="white">
              Şifremi Unuttum
            </Anchor>
          </Group>
        </Stack>
      </Form>


    </div>
  );
}